import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import SectionWithImage from "../../shared/SectionWithImage";
import { ParagraphText } from "../../shared/typography/Typography";
import Button from "../../shared/Button";
import AppStoreButtons from "./AppStoreButtons";
import QRCode from "./QRCode";
import "./DownloadPageTemplate.scss";

/**
 * Download Page Template
 * 
 * This is a reusable template for creating branded campaign download pages.
 * To create a new page:
 * 1. Copy this file to a new file (e.g., BeavertownGame.js)
 * 2. Update the component name
 * 3. Update the configurable links below
 * 4. Update SEO content in the Helmet section
 * 5. Add route to Router.js: <Route path="/beavertown-game" component={BeavertownGame} />
 * 
 * ============================================================================
 * CONFIGURABLE LINKS - Update these when duplicating the template
 * ============================================================================
 */

// QR Code URL - supports Branch links (e.g., "https://dusk.app.link/campaign-name"), 
// custom URL schemes (e.g., "dusk://branded-content/campaign-name"), or smart links
const QR_CODE_URL = "https://share.dusk.app/";

// iOS App Store link - supports deep link parameters (e.g., "?ct=campaign-name")
const IOS_APP_STORE_URL = "https://apps.apple.com/gb/app/dusk/id824241550";

// Android Google Play link - supports intent URLs for deep linking
const ANDROID_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.drinki.app&hl=en_GB";

// Deep link argument for Apple Smart Banner
// Supports Branch links (recommended): "https://dusk.app.link/campaign-name" or "https://dusk.app.link/campaign-name?param=value"
// Also supports custom URL schemes: "dusk://branded-content/campaign-name"
// When user taps the smart banner, iOS will open the app with this link as the argument
// Branch SDK in the app will handle routing to the correct screen
const APPLE_SMART_BANNER_DEEP_LINK = "https://dusk.app.link/campaign-name";

// Fallback download link (displayed with copy button)
const FALLBACK_DOWNLOAD_LINK = "https://share.dusk.app/";

// ============================================================================
// SEO CONFIGURATION - Update these when duplicating the template
// ============================================================================

// Page title (appears in browser tab and search results)
const PAGE_TITLE = "Download DUSK App – Free Drinks & Bar Rewards";

// Meta description (appears in search results)
const META_DESCRIPTION =
  "Download the DUSK app for free drinks, exclusive bar rewards and nightlife offers across the best bars in UK cities on iOS and Android.";

// Open Graph image (for social media sharing)
const OG_IMAGE = "https://static.dusk.app/website/share-img.png";

// Canonical URL (current page URL - update with actual route)
// This will be dynamically set based on window.location, but can be overridden
const CANONICAL_URL = "https://dusk.app/download";

// ============================================================================
// PAGE CONTENT - Update these when duplicating the template
// ============================================================================

// Hero heading
const HERO_HEADING = "DOWNLOAD DUSK";

// Hero subtext
const HERO_SUBTEXT = "Scan the QR code on your phone, or tap one of the buttons below to get started.";

// Fallback link text
const FALLBACK_TEXT = "Having trouble? Copy this link";

// Footer text is hardcoded in JSX below for better maintainability

/**
 * DownloadPageTemplate Component
 * 
 * Main template component with all sections: Hero, QR Code, App Store Buttons, 
 * Fallback Link, and Footer.
 */
const DownloadPageTemplate = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async (e) => {
    // Prevent any default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Only add https:// if the link doesn't already have a protocol
    const textToCopy = FALLBACK_DOWNLOAD_LINK.startsWith('http://') || FALLBACK_DOWNLOAD_LINK.startsWith('https://')
      ? FALLBACK_DOWNLOAD_LINK
      : `https://${FALLBACK_DOWNLOAD_LINK}`;

    // For iOS Safari, use synchronous fallback method immediately
    // iOS Safari requires clipboard operations to happen synchronously within user gesture
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // Use fallback method for iOS
      try {
        // Create a temporary textarea element
        const textarea = document.createElement("textarea");
        textarea.value = textToCopy;
        textarea.style.position = "fixed";
        textarea.style.left = "-999999px";
        textarea.style.top = "-999999px";
        textarea.setAttribute("readonly", "");
        document.body.appendChild(textarea);

        // Select and copy - must be synchronous
        textarea.select();
        textarea.setSelectionRange(0, textToCopy.length);

        const successful = document.execCommand("copy");

        // Clean up
        document.body.removeChild(textarea);

        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          console.error("Failed to copy using execCommand");
        }
      } catch (err) {
        console.error("Failed to copy link on iOS:", err);
      }
      return;
    }

    // For other browsers, try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.warn("Clipboard API failed, trying fallback:", err);
        // Fall through to fallback method
      }
    }

    // Fallback method for other browsers
    try {
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      textarea.style.position = "fixed";
      textarea.style.left = "-999999px";
      textarea.style.top = "-999999px";
      textarea.setAttribute("readonly", "");
      document.body.appendChild(textarea);

      textarea.select();
      textarea.setSelectionRange(0, textToCopy.length);

      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Failed to copy using execCommand");
      }
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // Get current page URL for canonical and Open Graph
  const currentUrl = typeof window !== "undefined"
    ? window.location.href
    : CANONICAL_URL;

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>{PAGE_TITLE} | DUSK</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="DUSK" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />

        {/* Apple Smart Banner */}
        <meta
          name="apple-itunes-app"
          content={`app-id=824241550, app-argument=${APPLE_SMART_BANNER_DEEP_LINK}`}
        />

        {/* Android Chrome Banner / Google Play App */}
        {/* Note: This meta tag doesn't support deep link arguments directly.
            For Branch deep linking on Android, use Branch's deferred deep linking:
            - When users install from Play Store, Branch SDK will pass deep link data after install
            - Ensure your app store button links and QR codes use Branch links for consistency */}
        <meta
          name="google-play-app"
          content="app-id=com.drinki.app"
        />

        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DUSK",
            "description": META_DESCRIPTION,
            "url": currentUrl,
            "applicationCategory": "LifestyleApplication",
            "operatingSystem": ["iOS", "Android"],
            "downloadUrl": [
              IOS_APP_STORE_URL,
              ANDROID_PLAY_STORE_URL
            ],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "GBP"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": 19000,
              "reviewCount": 19000
            },
            "publisher": {
              "@type": "Organization",
              "name": "DUSK"
            },
            "sameAs": [
              "https://www.instagram.com/dusk.app",
              "https://twitter.com/dusk_app",
              "https://www.facebook.com/downloadDUSK"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <SectionWithImage
        id="download-hero"
        className="is-flex is-align-items-center is-flex-direction-column is-justify-content-center"
      >
        <h1 className="dusk-h1-110 h1-text-shadow mb-md-4">{HERO_HEADING}</h1>
        <div className="download-subtitle-container">
          <p className="dusk-p-18 has-text-centered mb-md-6">{HERO_SUBTEXT}</p>
        </div>

        {/* QR Code */}
        <div className="download-qr-wrapper">
          <QRCode url={QR_CODE_URL} />
        </div>

        {/* App Store Buttons */}
        <AppStoreButtons
          iosUrl={IOS_APP_STORE_URL}
          androidUrl={ANDROID_PLAY_STORE_URL}
        />

        {/* Fallback Link Section */}
        <div className="download-fallback-section">
          <div className="has-text-centered">
            <p className="dusk-p-16 mb-3">{FALLBACK_TEXT}</p>
            <div className="fallback-link-container">
              <code className="dusk-p-14 fallback-link-text">
                {FALLBACK_DOWNLOAD_LINK}
              </code>
              <Button
                small
                onClick={handleCopyLink}
                aria-label="Copy download link"
                className="fallback-copy-button"
                type="button"
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        </div>
      </SectionWithImage>

      {/* Benefits / Explainer Copy */}
      <div className="download-subtitle-container download-benefits-text">
        <ParagraphText className="dusk-p-16 has-text-centered mb-md-6">
          DUSK is a nightlife and bar rewards app that gives you free drinks, exclusive offers and rewards at the
          best bars in major UK cities. Discover new places to go out, collect rewards as you drink, and get the app
          free on iOS and Android.
        </ParagraphText>
        <ParagraphText className="dusk-p-16 has-text-centered mb-md-6">
          Learn more about{" "}
          <Link to="/" className="download-footer-link">
            how DUSK works
          </Link>
          , explore{" "}
          <Link to="/for-bars" className="download-footer-link">
            DUSK for bars
          </Link>
          , discover{" "}
          <Link to="/for-brands" className="download-footer-link">
            DUSK for brands
          </Link>{" "}
          and{" "}
          <Link to="/for-rewards" className="download-footer-link">
            rewards for brand partners
          </Link>
          .
        </ParagraphText>
      </div>

      {/* Footer Section */}
      <section className="section download-footer-section">
        <div className="container">
          <div className="has-text-centered">
            <ParagraphText className="dusk-p-14 download-footer-text">
              By downloading, you agree to our{" "}
              <Link to="/premiumterms" className="download-footer-link">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy-policy" className="download-footer-link">
                Privacy Policy
              </Link>
              .
            </ParagraphText>
          </div>
        </div>
      </section>
    </>
  );
};

export default DownloadPageTemplate;
